import type { AnalysisState } from '@aibi/contracts';
import { cn, EmptyState, IconButton, SimpleTooltip } from '@aibi/ui';
import { ArrowUpFromLine, LayoutDashboard, Maximize2, Minimize2 } from 'lucide-react';
import type { CSSProperties } from 'react';
import { CardBody } from '../../workspace/components/canvas/CardBody';
import { cardTitle } from '../../workspace/model/card-title';
import {
  cardRect,
  ROW_UNIT_REM,
} from '../../workspace/model/workspace-document';
import { useUiDispatch, useUiState } from '../../workspace/state/ui-state';
import { useWorkspace, useWorkspaceActions } from '../../workspace/state/workspace-context';

/**
 * Lienzo de solo lectura: las visualizaciones del reporte en la misma rejilla que el
 * workspace, sin arrastrar ni editar campos. Seleccionar valores, profundizar y maximizar
 * siguen disponibles para explorar.
 */
export function ReportCanvas() {
  const { document } = useWorkspace();
  const { maximizedCardId } = useUiState();
  const maximized = document.cards.find((card) => card.id === maximizedCardId);

  return (
    <main aria-label="Reporte" className="flex min-w-0 flex-1 flex-col bg-canvas">
      <div className="relative flex-1 overflow-auto p-4">
        {maximized ? (
          <div className="h-full">
            <ReportCard card={maximized} maximized />
          </div>
        ) : document.cards.length === 0 ? (
          <EmptyState
            icon={<LayoutDashboard />}
            title="Este reporte no tiene visualizaciones"
            description="Este reporte no incluye visualizaciones."
          />
        ) : (
          <div
            data-canvas-grid
            className="grid grid-cols-12 gap-3"
            style={{ gridAutoRows: `${String(ROW_UNIT_REM)}rem` }}
          >
            {document.cards.map((card) => (
              <ReportCard key={card.id} card={card} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

function ReportCard({
  card,
  maximized = false,
}: {
  readonly card: AnalysisState;
  readonly maximized?: boolean;
}) {
  const { catalog, index } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const { selectedCardId } = useUiState();
  const uiDispatch = useUiDispatch();
  const selected = selectedCardId === card.id;
  const title = cardTitle(card, catalog);
  const drilled = card.drilldown.filter((drill) => drill.path.length > 0);
  const rect = cardRect(card);
  const style = maximized
    ? undefined
    : ({
        gridColumn: `${String(rect.x + 1)} / span ${String(rect.w)}`,
        gridRow: `${String(rect.y + 1)} / span ${String(rect.h)}`,
      } satisfies CSSProperties);

  return (
    <article
      style={style}
      aria-label={title}
      aria-current={selected ? 'true' : undefined}
      data-card-id={card.id}
      className={cn(
        'group/card flex min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-card',
        maximized ? 'h-full' : 'col-span-12 h-full min-h-0 max-md:!col-span-12 max-md:!col-start-1',
        selected && 'ring-2 ring-primary',
      )}
      onClick={() => {
        uiDispatch({ type: 'select-card', cardId: card.id });
      }}
      onFocus={() => {
        uiDispatch({ type: 'select-card', cardId: card.id });
      }}
    >
      <header className="flex h-10 shrink-0 items-center gap-1 border-b border-border/70 pl-3 pr-1.5">
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium" title={title}>
          {title}
        </h3>
        {drilled.map((drill) => (
          <SimpleTooltip key={drill.hierarchyId} content="Subir un nivel en la jerarquia">
            <IconButton
              label={`Subir nivel en ${index.getHierarchy(drill.hierarchyId)?.name ?? drill.hierarchyId}`}
              onClick={() => {
                execute({ type: 'drill-up', cardId: card.id, hierarchyId: drill.hierarchyId });
              }}
            >
              <ArrowUpFromLine />
            </IconButton>
          </SimpleTooltip>
        ))}
        <SimpleTooltip content={maximized ? 'Restaurar' : 'Maximizar'}>
          <IconButton
            label={maximized ? 'Restaurar tamano' : 'Maximizar visualizacion'}
            active={maximized}
            onClick={() => {
              uiDispatch({ type: 'toggle-maximize', cardId: card.id });
            }}
          >
            {maximized ? <Minimize2 /> : <Maximize2 />}
          </IconButton>
        </SimpleTooltip>
      </header>
      <div className="min-h-0 flex-1">
        <CardBody card={card} title={title} />
      </div>
    </article>
  );
}
